import { useState } from 'react';
import { Combobox } from '@/components/shared/combobox';
import { companyName } from '@/lib/org-labels';
import type { PositionListItem } from '@/types';

type LinkData = { position_id: number; is_required: boolean; priority: number };

// Formulario colapsable para vincular un puesto a la capacitación. Auto-contenido
// (maneja su propio estado); el padre lo monta/desmonta. Extraído por ENG-LOC-01.
export function LinkPositionForm({
  availablePositions,
  isPending,
  error,
  onSubmit,
  onCancel,
}: {
  availablePositions: PositionListItem[];
  isPending: boolean;
  error?: { message: string } | null;
  onSubmit: (data: LinkData) => void;
  onCancel: () => void;
}) {
  const [selectedPositionId, setSelectedPositionId] = useState<string>('');
  const [isRequired, setIsRequired] = useState(true);
  const [priority, setPriority] = useState(0);

  const handleLink = () => {
    if (!selectedPositionId) return;
    onSubmit({ position_id: Number(selectedPositionId), is_required: isRequired, priority });
  };

  return (
    <div className="mb-4 rounded-lg border border-border bg-muted/20 p-4">
      <div className="space-y-3">
        <div>
          <label className="mb-1.5 block text-sm font-medium text-foreground">Puesto</label>
          <Combobox
            value={selectedPositionId}
            onChange={setSelectedPositionId}
            options={availablePositions.map((pos) => ({
              value: String(pos.id),
              label: pos.name,
              sublabel: `${pos.department.name} (${companyName(pos.department.company)})`,
            }))}
            placeholder="Seleccionar puesto..."
            clearable
          />
        </div>
        <div className="flex items-center gap-6">
          <label className="flex items-center gap-2 text-sm text-foreground">
            <input
              type="checkbox"
              checked={isRequired}
              onChange={(e) => setIsRequired(e.target.checked)}
              className="rounded border-input"
            />
            Obligatorio
          </label>
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-foreground">Prioridad</label>
            <input
              type="number"
              min={0}
              value={priority}
              onChange={(e) => setPriority(Number(e.target.value))}
              className="w-20 rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring"
            />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleLink}
            disabled={isPending || !selectedPositionId}
            className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50"
          >
            {isPending ? 'Vinculando...' : 'Vincular'}
          </button>
          <button
            onClick={onCancel}
            className="rounded-lg border border-border px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Cancelar
          </button>
        </div>
        {error && <p className="text-xs text-destructive">{error.message}</p>}
      </div>
    </div>
  );
}
